import React from 'react';
import Navbar from '../components/Navbar';
import ContentSplitSections from '../components/ContentSplitSections';
import { Link } from 'react-router-dom';
import '../styles/flower.css';
import CTASection from '../components/CTASection';

// Flower and garden service sections
const gardenServices = [
  {
    title: "Annual & Perennial Gardens",
    text: `Color That Transforms Your Landscape\n\nFrom vibrant summer annuals to hardy mountain perennials, we design and install flower gardens that thrive in The Valley's unique climate. Our expert team selects plants that not only survive but flourish at high altitude, ensuring continuous blooms throughout the growing season.\n\nGarden Services:\n• Custom flower bed design\n• Spring and fall planting\n• Native and adapted plant selection\n• Seasonal color rotations\n• Container gardens and planters`,
    image: "/images/flower/redWhiteBloom.jpg",
    alt: "Perennial Garden Design",
    reverse: false
  },
  {
    title: "Alpine & Rock Garden Flowers",
    text: `Beauty That Thrives in Stone\n\nThese remarkable alpine flowers bring stunning color and fragrance to the most challenging terrain. Delicate blooms nestle into rocky crevices, cascade over stone walls, and fill mountain gardens with their sweet perfume. Perfect for rock gardens, xeriscape designs, and naturalistic landscapes where hardy beauty meets low maintenance.\n\nAlpine Garden Features:\n• Fragrant purple blooms that perfume the air\n• Drought-tolerant once established\n• Thrives in rocky, well-drained soils\n• Attracts butterflies and pollinators\n• Returns year after year with minimal care`,
    image: "/images/flower/purpleSpike.JPG",
    alt: "Alpine Rock Garden Flowers",
    reverse: true
  },
  {
    title: "Commercial & HOA Landscaping",
    text: `Sophisticated Outdoor Spaces for Communities\n\nWe create and maintain refined landscapes for commercial properties, homeowner associations, and residential communities. From mature tree canopies and naturalized creek corridors to vibrant hanging baskets and professionally maintained beds, we deliver polished landscapes that enhance property values and resident satisfaction.\n\nComplete Property Care:\n• Mature tree selection and care\n• Seasonal hanging flower displays\n• Creek and waterway naturalization\n• Professional mulching and bed maintenance\n• Year-round property enhancement programs`,
    image: "/images/maintenance/2people.JPG",
    alt: "Commercial HOA Landscape Management",
    reverse: false
  }
];

const seasonalServices = [
  {
    title: "Garden Health & Treatment Programs",
    text: `Comprehensive Care for Thriving Blooms\n\nHealthy flowers start with healthy soil and proactive care. Our specialized treatment programs keep your gardens vibrant and disease-free through targeted fertilization, integrated pest management, and soil health improvement. We use eco-conscious methods to protect your investment while maintaining The Valley's pristine environment.\n\nTreatment Services:\n• Custom fertilization programs for optimal blooms\n• Integrated pest and disease management\n• Soil testing and amendment recommendations\n• Selective weed control in flower beds\n• Eco-friendly treatment options\n• Warranty-backed results`,
    image: "/images/flower/flowerBed.png",
    alt: "Garden Health Programs",
    reverse: false
  },
  {
    title: "Greenhouse-Grown Plant Selection",
    text: `Locally Adapted Plants for Mountain Success\n\nOur on-site greenhouse allows us to cultivate and acclimate plants specifically for The Valley's unique conditions. We carefully select varieties that thrive at altitude, pre-condition them to local microclimates, and design custom plant palettes that ensure your garden flourishes from day one.\n\nGreenhouse Advantages:\n• Plants pre-adapted to high altitude conditions\n• Custom color palettes designed for your property\n• Seasonal variety selection and availability\n• Hardy perennials and annual rotations\n• Expert consultation on plant performance\n• Year-round planning and preparation`,
    image: "/images/flower/greenHouseFlowers.jpg",
    alt: "Greenhouse Plant Selection",
    reverse: true
  }
];

const Flower = () => {
  return (
    <div className="flower-page">
      <Navbar />

      {/* Hero Section */}
      <section className="flower-hero">
        <img
          src="/images/flower/flowerHeroImage.png"
          alt="Beautiful Flower Garden"
          className="hero-image"
          loading="eager"
          decoding="async"
        />
        <div className="hero-overlay"></div>
        <div className="flower-hero-content">
          <h1 className="flower-hero-title">Flower Gardens & Colorful Landscapes</h1>
          <p className="flower-hero-subtitle">
            Creating stunning gardens that bring color, fragrance, and joy to your property
          </p>
          <Link to="/gallery/flower" className="btn-cta">
            Gallery
          </Link>
        </div>
      </section>

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-inner text-center">
          <h2 className="section-title">Bringing Life & Color to Mountain Landscapes</h2>
          <p className="intro-text">
            In The Valley's challenging mountain climate, creating beautiful flower gardens requires
            expertise, experience, and a deep understanding of what thrives at altitude. All Seasons
            Landscaping combines artistic design with horticultural knowledge to create gardens that
            not only survive but flourish in our unique environment. From mountain estate gardens
            to sophisticated perennial borders, we bring your floral vision to life.
          </p>
        </div>
      </section>

      {/* Garden Services */}
      <section className="section bg-soft-petal">
        <div className="section-inner">
          <h2 className="section-title text-center">Garden Design & Installation</h2>
          <p className="section-intro">From concept to bloom, we create gardens that enhance your outdoor living</p>
        </div>
      </section>

      <ContentSplitSections sections={gardenServices} />

      {/* Seasonal Services */}
      <section className="section bg-white">
        <div className="section-inner">
          <h2 className="section-title text-center">Year-Round Garden Care</h2>
        </div>
      </section>

      <ContentSplitSections sections={seasonalServices} />

      {/* Design Styles Section */}
      <section className="section bg-soft-petal">
        <div className="section-inner">
          <h2 className="section-title text-center">Garden Styles We Create</h2>
          <div className="style-grid">
            <div className="style-card">
              <h3>Mountain Estate Gardens</h3>
              <p>Refined alpine plantings that complement mountain architecture</p>
            </div>
            <div className="style-card">
              <h3>Modern Minimalist</h3>
              <p>Clean lines with bold plant choices and structured design</p>
            </div>
            <div className="style-card">
              <h3>Mountain Meadow</h3>
              <p>Naturalistic plantings that blend with the landscape</p>
            </div>
            <div className="style-card">
              <h3>Formal Borders</h3>
              <p>Structured designs with repeated patterns and symmetry</p>
            </div>
            <div className="style-card">
              <h3>Rock Gardens</h3>
              <p>Alpine plants combined with natural stone features</p>
            </div>
            <div className="style-card">
              <h3>Water-Wise</h3>
              <p>Beautiful gardens that conserve our precious water</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section - Styled like Light.js */}
      <section className="section bg-white">
        <div className="section-inner">
          <h2 className="section-title text-center">Our Garden Design Process</h2>
          <div className="flower-process-timeline">
            <div className="flower-timeline-container">
              <div className="flower-timeline-item">
                <div className="flower-timeline-number">1</div>
                <div className="flower-timeline-content">
                  <h3>Consultation</h3>
                  <p>Understand your vision, lifestyle, and garden goals</p>
                </div>
              </div>
              <div className="flower-timeline-item">
                <div className="flower-timeline-number">2</div>
                <div className="flower-timeline-content">
                  <h3>Site Analysis</h3>
                  <p>Evaluate sun, soil, drainage, and existing features</p>
                </div>
              </div>
              <div className="flower-timeline-item">
                <div className="flower-timeline-number">3</div>
                <div className="flower-timeline-content">
                  <h3>Design Creation</h3>
                  <p>Develop custom plans with plant selections</p>
                </div>
              </div>
              <div className="flower-timeline-item">
                <div className="flower-timeline-number">4</div>
                <div className="flower-timeline-content">
                  <h3>Installation</h3>
                  <p>Professional planting with proper techniques</p>
                </div>
              </div>
              <div className="flower-timeline-item">
                <div className="flower-timeline-number">5</div>
                <div className="flower-timeline-content">
                  <h3>Garden Care</h3>
                  <p>Ongoing maintenance for lasting beauty</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
     <CTASection
  title="Ready to Create Your Dream Garden?"
  subtitle=" Let's design a garden that brings joy through every season. From spring bulbs to fall colors,
            we'll create an outdoor space you'll love for years to come."
  galleryLink="/Gallery/flower"
  showEmergency={false}
/>

    </div>
  );
};

export default Flower;
